import {
  useRef,
  useEffect,
  useState,
  PointerEvent as ReactPointerEvent,
  KeyboardEvent as ReactKeyboardEvent,
} from "react";
import { formatAmplienceImageURL } from "@JohnLewisPartnership/jl-design-system/dist/utils/imageUrlModifier/imageUrlModifier";
import {
  TransformWrapper,
  TransformComponent,
  ReactZoomPanPinchRef,
} from "react-zoom-pan-pinch";
import { GalleryObject, ImageClickPosition } from "../utils/utils.ts";
import { dispatchGalleryMonetateQEvent } from "@/components/Galleries/components/Analytics/dispatchGalleryMonetateQEvent";
import styles from "./SuperZoomImage.module.scss";

interface SuperZoomImageProps {
  mediaItems: GalleryObject[];
  selectedIndex: number;
  imageClickPosition: ImageClickPosition;
  onCloseClick: (
    event:
      ReactPointerEvent<HTMLDivElement> | ReactKeyboardEvent<HTMLDivElement>,
  ) => void;
  onCloseButton: () => void;
}

export const SuperZoomImage = ({
  mediaItems,
  selectedIndex,
  imageClickPosition,
  onCloseClick,
  onCloseButton,
}: SuperZoomImageProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const imageRef = useRef<HTMLImageElement>(null);
  const transformRef = useRef<ReactZoomPanPinchRef | null>(null);
  const [isMouseOver, setIsMouseOver] = useState(false);

  const setInitialTransform = () => {
    if (!imageRef.current) {
      return;
    }

    const windowHeight = window.innerHeight;
    const windowWidth = window.innerWidth;

    const { width: imageWidth, height: imageHeight } =
      imageRef.current.getBoundingClientRect();

    if (imageWidth <= windowWidth) {
      return;
    }

    const topBounds = windowHeight - imageHeight;
    const leftBounds = windowWidth - imageWidth;

    const xPos =
      imageClickPosition.x * -1 < leftBounds
        ? leftBounds
        : imageClickPosition.x * -1;
    const yPos =
      imageClickPosition.y * -1 < topBounds
        ? topBounds
        : imageClickPosition.y * -1;

    transformRef.current?.setTransform(xPos, yPos, 2, 0);
  };

  const handleFullscreenAnimationEnd = () => {
    setInitialTransform();
  };

  useEffect(() => {
    dispatchGalleryMonetateQEvent("SuperZoom", "imageModal");

    const modalElement = containerRef.current?.closest('[data-testid="modal"]');

    modalElement?.addEventListener(
      "animationstart",
      handleFullscreenAnimationEnd,
      {
        once: true,
      },
    );

    return () => {
      modalElement?.removeEventListener(
        "animationstart",
        handleFullscreenAnimationEnd,
      );
    };
  }, []);

  const mediaItem = mediaItems[selectedIndex];
  if (!mediaItem) {
    return null;
  }

  const srcSetValues = [
    "$rsp-pdp-port-320$ 320w",
    "$rsp-pdp-port-640$ 640w",
    "$rsp-pdp-port-1080$ 1080w",
    "$rsp-pdp-port-1440$ 1440w",
  ];

  const generateSrcSetUrl = (rspValue: string) =>
    formatAmplienceImageURL(`${mediaItem.imageUrl}?${rspValue}`);

  const handleMouseMove = (event: React.PointerEvent<HTMLDivElement>) => {
    event.preventDefault();

    if (event.pointerType !== "mouse") {
      setIsMouseOver(false);
      return;
    }
    setIsMouseOver(true);

    if (!containerRef.current || !imageRef.current) {
      return;
    }

    dispatchGalleryMonetateQEvent("SuperZoomPan", "imageModal");

    const {
      nativeEvent: { clientX, clientY },
    } = event;

    const containerBoundingClient =
      containerRef.current.getBoundingClientRect();
    const imageBoundingClient = imageRef.current.getBoundingClientRect();

    const offsetLeft = containerBoundingClient.left;
    const relativeMousePositionX = clientX - offsetLeft;
    const containerWidth = containerBoundingClient.width;
    const imageWidth = imageBoundingClient.width;
    const imageLeft =
      (relativeMousePositionX / containerWidth) * (imageWidth - containerWidth);

    const offsetTop = containerBoundingClient.top;
    const relativeMousePositionY = clientY - offsetTop;
    const containerHeight = containerBoundingClient.height;
    const imageHeight = imageBoundingClient.height;
    const imageTop =
      (relativeMousePositionY / containerHeight) *
      (imageHeight - containerHeight);

    imageRef.current.style = `transform: translate(-${imageLeft}px, -${imageTop}px)`;
  };

  const handleOnZoom = (ref: ReactZoomPanPinchRef) => {
    dispatchGalleryMonetateQEvent("SuperZoomPan", "imageModal");
    if (ref.state.scale <= 1) {
      onCloseButton();
    }
  };

  const handleStartPanning = () => {
    dispatchGalleryMonetateQEvent("SuperZoomPan", "imageModal");
  };

  return (
    <div
      ref={containerRef}
      className={styles.imageWrapper}
      aria-label={`Product image ${selectedIndex + 1}, ${mediaItem.title}`}
      data-testid={`gallery:super:zoom:image`}
      onPointerOver={handleMouseMove}
      onPointerMove={handleMouseMove}
      onPointerDown={onCloseClick}
      onPointerUp={onCloseClick}
      onPointerCancel={onCloseClick}
      onPointerOut={onCloseClick}
      onPointerLeave={onCloseClick}
      onKeyUp={onCloseClick}
    >
      {isMouseOver ? (
        <img
          ref={imageRef}
          src={formatAmplienceImageURL(mediaItem.imageUrl)}
          srcSet={`${srcSetValues.map((v) => generateSrcSetUrl(v)).join(",")}`}
          sizes="100vw"
          loading="eager"
          className={styles.image}
          alt={`Zoomed in product image ${mediaItem.title}`}
        />
      ) : (
        <TransformWrapper
          initialScale={2}
          minScale={1}
          maxScale={3}
          doubleClick={{ disabled: true }}
          onZoom={handleOnZoom}
          onPinch={handleOnZoom}
          onPanningStart={handleStartPanning}
          ref={transformRef}
        >
          <TransformComponent wrapperClass={styles.imageContainer}>
            <img
              ref={imageRef}
              src={formatAmplienceImageURL(mediaItem.imageUrl)}
              srcSet={`${srcSetValues.map((v) => generateSrcSetUrl(v)).join(",")}`}
              sizes="100vw"
              loading="eager"
              className={styles.image}
              alt={`Zoomed in product image ${mediaItem.title}`}
            />
          </TransformComponent>
        </TransformWrapper>
      )}
    </div>
  );
};
